/* ============================================================
   SchedulerAccountsCard — TASK-319. The one thing missing from the
   dashboard for a Posting (Scheduler) account: a summary of which social
   accounts are connected. Today that only exists inside Settings; this
   card is a read-only, pure-presentational summary fed entirely from data
   the dashboard already loads (useDashboardData's socialAccounts) — NO new
   fetch, and specifically no call to api.billing.getPostingQuote (that
   stays PostingSubscriptionCard's Settings-only concern, which needs a
   live Stripe quote).

   Owner decision 2026-09-07 (item 2): the dashboard must not talk money —
   billing lives in Settings -> Plan & billing (PostingSubscriptionCard).
   The per-account price line ("€3.99 x 1 = €3.99/month", "Billed quantity
   remains 1...") that used to sit under both variants below is gone; this
   card is now just the compact accounts list + a "Manage accounts ->"
   link, and visually lighter (`sched-accts-card` modifier, see
   dashboard.css) so it doesn't dominate above the stats.

   Two variants:
   - empty (0 accounts): CTA to connect the first one.
   - populated: one row per BILLED account (platform icon + label + status
     pill).
   ============================================================ */
import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icons as I } from '../pack-icons';
import { BrandIcons } from '../pack-icons/brands';
import { useDashboardData } from '../../state/DashboardDataContext.jsx';

// Duplicated on purpose: postingPlan.js's own NOT_CONNECTED_STATUSES isn't
// exported and that file is out of scope for this worktree (owned by
// another in-flight task) — kept as the same two literal strings so this
// card lists exactly the same accounts lib/postingPlan.js's billing helpers
// (used elsewhere, e.g. PostingSubscriptionCard in Settings) treat as billed.
const NOT_CONNECTED_STATUSES = new Set(['disconnected', 'revoked']);
// Still billed (not in NOT_CONNECTED_STATUSES above) but needs the owner's
// attention — matches the statuses Dashboard.jsx's deriveAttentionItems
// already treats as "needs reconnect" for social accounts.
const NEEDS_RECONNECT_STATUSES = new Set(['needs_reconnect', 'needs_manual_action', 'expired', 'token_expired']);

// postingPlan.js has no platform-label helper on this branch (Settings.jsx
// has its own local platformLabelOf, not exported/shared) — small local map
// so the sub-line reads "TikTok" / "LinkedIn" instead of a raw lowercase key.
function platformLabelOf(p) {
  const v = String(p || '').toLowerCase();
  if (v === 'x') return 'X';
  if (v === 'tiktok') return 'TikTok';
  if (v === 'linkedin') return 'LinkedIn';
  return v ? v.charAt(0).toUpperCase() + v.slice(1) : 'Account';
}

export default function SchedulerAccountsCard({ onConnect, connectDisabled = false } = {}) {
  const { t } = useTranslation();
  const dash = useDashboardData() || {};

  // Fix list item 4: /social/accounts hasn't resolved yet — without this
  // guard every paying Scheduler account flashes "No accounts connected
  // yet" for one tick even when accounts already exist.
  if (!dash.fetchedAt?.socialAccounts) return null;

  const accounts = dash.socialAccounts || [];
  // N4 (judge re-verify): every remaining row disconnected/revoked → nothing is
  // billed, so the populated variant would render an empty account list.
  // Treat it as the empty state instead.
  const hasBilledAccount = accounts.some((acc) => !NOT_CONNECTED_STATUSES.has(typeof acc?.status === 'string' ? acc.status.toLowerCase() : ''));

  if (accounts.length === 0 || !hasBilledAccount) {
    return (
      <section className="card attn-panel attn-clear sched-accts-card">
        <div className="attn-clear-row">
          <span className="attn-clear-ico attn-clear-ico-neutral"><I.link size={16} /></span>
          <div className="attn-clear-text">
            <h3>{t('schedulerDash.accountsEmptyTitle')}</h3>
            <p>{t('schedulerDash.accountsEmptySub')}</p>
          </div>
        </div>
        {onConnect ? <button type="button" onClick={onConnect} disabled={connectDisabled} className="btn btn-primary btn-sm" style={{ marginTop: 12 }}>
          {t('schedulerDash.accountsCta')} <I.arrowRight size={11} />
        </button> : <Link to="/app/settings?tab=social" className="btn btn-primary btn-sm" style={{ marginTop: 12 }}>
          {t('schedulerDash.accountsCta')} <I.arrowRight size={11} />
        </Link>}
      </section>
    );
  }

  // Fix list item 9: rows shown must match what's billed — exclude
  // disconnected/revoked rows entirely rather than listing them with some
  // status pill, and never bill/display a quantity of 0 while at least one
  // (even if not-yet-classified) account row exists.
  const billedAccounts = accounts.filter((acc) => {
    const status = typeof acc?.status === 'string' ? acc.status.toLowerCase() : '';
    return !NOT_CONNECTED_STATUSES.has(status);
  });

  return (
    <section className="card attn-panel sched-accts-card">
      <div className="attn-head">
        <div>
          <div className="eyebrow"><span>{t('schedulerDash.accountsEyebrow')}</span></div>
          <h3>{t('schedulerDash.accountsTitle')}</h3>
        </div>
        <Link to="/app/settings?tab=social" className="week-link">
          {t('schedulerDash.accountsManage')} <I.arrowRight size={12} />
        </Link>
      </div>
      <div className="attn-list">
        {billedAccounts.map((acc, i) => {
          const status = typeof acc?.status === 'string' ? acc.status.toLowerCase() : '';
          const needsReconnect = NEEDS_RECONNECT_STATUSES.has(status);
          const platform = String(acc?.platform || '').toLowerCase();
          const label = acc?.displayName || acc?.externalAccountId || platformLabelOf(platform);
          return (
            <div key={acc.id || `${platform}-${i}`} className="attn-item">
              <span className="attn-ico attn-ico-neutral">{BrandIcons[platform]?.({ size: 14 })}</span>
              <div className="attn-text">
                <div className="attn-title attn-title-clip">{label}</div>
                <div className="attn-sub">{platformLabelOf(platform)}</div>
              </div>
              {needsReconnect
                ? <span className="pill pill-warning">{t('schedulerDash.statusReconnect')}</span>
                : <span className="pill pill-success">{t('schedulerDash.statusConnected')}</span>}
            </div>
          );
        })}
      </div>
    </section>
  );
}
